import { ReactNode } from "react";

/**
 * Alert component matching AngularJS Bootstrap alert styles
 *
 * Source of truth from Bootstrap CSS:
 *
 * .alert {
 *   padding: 15px;
 *   margin-bottom: 20px;
 *   border: 1px solid transparent;
 *   border-radius: 4px;
 * }
 *
 * .alert-success {
 *   color: #3c763d;
 *   background-color: #dff0d8;
 *   border-color: #d6e9c6;
 * }
 *
 * .alert-info {
 *   color: #31708f;
 *   background-color: #d9edf7;
 *   border-color: #bce8f1;
 * }
 *
 * .alert-warning {
 *   color: #8a6d3b;
 *   background-color: #fcf8e3;
 *   border-color: #faebcc;
 * }
 *
 * .alert-danger {
 *   color: #a94442;
 *   background-color: #f2dede;
 *   border-color: #ebccd1;
 * }
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiAlertProps {
  children: ReactNode;
  /** Alert severity matching Bootstrap classes: success, info, warning, error (danger) */
  severity?: "success" | "info" | "warning" | "error";
  /** Optional close handler - shows close button when provided */
  onClose?: () => void;
  className?: string;
  /** Optional title for the alert */
  title?: string;
}

export const UiAlert = ({
  children,
  severity = "info",
  onClose,
  className = "",
  title,
}: UiAlertProps) => {
  // Map 'error' to 'danger' to match Bootstrap naming
  const bootstrapSeverity = severity === "error" ? "danger" : severity;

  // Base classes matching .alert
  const baseClasses = [
    "p-[15px]", // padding: 15px
    "mb-[20px]", // margin-bottom: 20px
    "border", // border: 1px solid
    "border-solid",
    "rounded-[4px]", // border-radius: 4px
    "relative", // for close button positioning
  ];

  // Severity-specific colors matching Bootstrap
  const severityClasses = {
    success: [
      "text-[#3c763d]", // color: #3c763d
      "bg-[#dff0d8]", // background-color: #dff0d8
      "border-[#d6e9c6]", // border-color: #d6e9c6
    ],
    info: [
      "text-[#31708f]", // color: #31708f
      "bg-[#d9edf7]", // background-color: #d9edf7
      "border-[#bce8f1]", // border-color: #bce8f1
    ],
    warning: [
      "text-[#8a6d3b]", // color: #8a6d3b
      "bg-[#fcf8e3]", // background-color: #fcf8e3
      "border-[#faebcc]", // border-color: #faebcc
    ],
    danger: [
      "text-[#a94442]", // color: #a94442
      "bg-[#f2dede]", // background-color: #f2dede
      "border-[#ebccd1]", // border-color: #ebccd1
    ],
  };

  const allClasses = [
    ...baseClasses,
    ...severityClasses[bootstrapSeverity],
    onClose ? "pr-[35px]" : "", // .alert-dismissible padding-right: 35px
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={allClasses} role="alert">
      {onClose && (
        <button
          type="button"
          className="absolute top-[-2px] right-[-21px] p-0 bg-transparent border-0
            text-inherit cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
          onClick={onClose}
          aria-label="Close"
        >
          <span
            aria-hidden="true"
            className="text-[21px] font-bold leading-[1]"
          >
            &times;
          </span>
        </button>
      )}

      {title && (
        <h4 className="mt-0 mb-2 text-inherit font-semibold">{title}</h4>
      )}

      <div className="[&>p]:mb-0 [&>ul]:mb-0 [&>p+p]:mt-[5px]">{children}</div>
    </div>
  );
};
